/**
 * Transmettre la signature à une autre personne (Franck, 15/10/2026) : la
 * secrétaire reçoit le lien, le patron signe.
 *
 * Lot 2bis : selon le réglage de l'émetteur pour CE destinataire,
 * - « autoriser » : la transmission est immédiate, ce lien cesse de servir ;
 * - « demander » : l'émetteur valide d'abord (écran d'attente, EnAttente) ;
 * - « interdire » : l'écran le dit et renvoie vers l'émetteur.
 * Prénom et nom séparés (en avancée : exactement comme sur la pièce) ;
 * numéro de mobile quand la ligne signe par code SMS. Deux transmissions au
 * plus par ligne.
 */
import { useState } from "react";

import { ApiError, transmettre, type SignerView } from "../api";
import { Ico } from "../components/Icone";
import type { t } from "../i18n";

interface Props {
  token: string;
  view: SignerView;
  L: ReturnType<typeof t>;
  onCancel: () => void;
  /** Demande envoyée : la vue se recharge et montre l'attente. */
  onDemandee: () => void;
}

const E164 = /^\+[1-9][0-9]{6,14}$/;

export function Transmettre({ token, view, L, onCancel, onDemandee }: Props) {
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState(false);

  const client = view.issuer_name ?? "L'émetteur";
  const reglage = view.transmission ?? "interdire";
  const avecTelephone = Boolean(view.transmission_telephone);
  const tel = telephone.replace(/\s+/g, "");

  if (fait) {
    return (
      <section className="card stack carte-etroite attente attente--fait" aria-live="polite">
        <span className="attente__signe"><Ico nom="envoi" taille={24} /></span>
        <h1 className="card__title">{L.transmisTitre}</h1>
        <p className="card__lead">{L.transmisCorps(`${prenom.trim()} ${nom.trim()}`, email.trim())}</p>
      </section>
    );
  }

  if (reglage === "interdire" || (view.transmissions_restantes ?? 0) <= 0) {
    return (
      <section className="card stack carte-etroite">
        <h1 className="card__title">{L.transmettreImpossible}</h1>
        <p className="alert alert--info" role="status">
          {reglage === "interdire" ? L.transmettreInterdit(client) : L.transmettreLimite}
        </p>
        <button type="button" className="btn btn--secondary btn--bloc" onClick={onCancel}>
          <Ico nom="gauche" /> {L.retourAuDocument}
        </button>
      </section>
    );
  }

  const valide = prenom.trim() && nom.trim() && /^\S+@\S+\.\S+$/.test(email.trim()) && (!avecTelephone || E164.test(tel));

  return (
    <section className="card stack carte-etroite">
      <div>
        <h1 className="card__title">{L.transmettreTitre}</h1>
        <p className="card__lead">{L.transmettreAide}</p>
      </div>
      <form className="formulaire" noValidate onSubmit={(e) => {
        e.preventDefault();
        if (envoi) return;
        if (!valide) {
          setErreur(L.transmettreIncomplet(avecTelephone));
          return;
        }
        setEnvoi(true);
        setErreur(null);
        transmettre(token, { prenom: prenom.trim(), nom: nom.trim(), email: email.trim(),
                             telephone: avecTelephone ? tel : undefined })
          .then((etat) => (etat === "demandee" ? onDemandee() : setFait(true)),
                (x) => setErreur(x instanceof ApiError && x.detail ? x.detail : L.errors.server))
          .finally(() => setEnvoi(false));
      }}>
        <div className="champ">
          <div className="champs-2">
            <div className="champ">
              <label className="field-label" htmlFor="transmettre-prenom">{L.transmettrePrenom}</label>
              <input id="transmettre-prenom" className="saisie" value={prenom} autoComplete="off"
                     autoCapitalize="words" onChange={(e) => setPrenom(e.target.value)} />
            </div>
            <div className="champ">
              <label className="field-label" htmlFor="transmettre-nom">{L.transmettreNomFamille}</label>
              <input id="transmettre-nom" className="saisie" value={nom} autoComplete="off"
                     autoCapitalize="words" onChange={(e) => setNom(e.target.value)} />
            </div>
          </div>
          {view.cle_acces && <p className="champ__aide">{L.transmettreIdentiteAide}</p>}
        </div>
        <div className="champ">
          <label className="field-label" htmlFor="transmettre-email">{L.transmettreEmail}</label>
          <input id="transmettre-email" className="saisie" type="email" inputMode="email" value={email}
                 autoComplete="off" onChange={(e) => setEmail(e.target.value)} aria-describedby="transmettre-email-aide" />
          <p className="champ__aide" id="transmettre-email-aide">{L.transmettreEmailAide}</p>
        </div>
        {avecTelephone && (
          <div className="champ">
            <label className="field-label" htmlFor="transmettre-tel">{L.transmettreTelephone}</label>
            <input id="transmettre-tel" className="saisie" type="tel" inputMode="tel" value={telephone}
                   placeholder="+33612345678" autoComplete="off" onChange={(e) => setTelephone(e.target.value)}
                   aria-invalid={telephone !== "" && !E164.test(tel) ? true : undefined}
                   aria-describedby="transmettre-tel-aide" />
            <p className="champ__aide" id="transmettre-tel-aide">{L.transmettreTelephoneAide}</p>
          </div>
        )}
        {reglage === "demander" && <p className="alert alert--info" role="status">{L.transmettreDemanderNote(client)}</p>}
        {erreur && <p className="alert alert--error" role="alert">{erreur}</p>}
        <button type="submit" className="btn btn--primary btn--bloc" disabled={envoi}>
          {reglage === "demander" ? L.transmettreDemander : L.transmettreValider}
        </button>
      </form>
      <div className="actions-discretes">
        <button type="button" className="btn btn--discret" onClick={onCancel}>{L.annuler}</button>
      </div>
    </section>
  );
}
